import type { CheckoutReady } from '../../bot-core/types/chat.types';
import { invalidateBotCheckoutCache } from './useBotCheckoutHandler';

const CART_MUTATING_TOOLS = new Set(['add_to_cart', 'remove_from_cart']);
const CHECKOUT_TOOLS = new Set(['initiate_checkout', 'generate_checkout_url']);

export interface BotTurnCompleteMeta {
  lastTool?: string | null;
  /** Every tool the turn ran, in order. Preferred over lastTool when present. */
  turnTools?: string[];
  checkoutReady?: CheckoutReady;
}

/** Shared post-turn hook: refresh itinerary + checkout payload when the cart changes. */
export async function handleBotTurnComplete(
  sessionId: string,
  meta: BotTurnCompleteMeta,
  refreshSelections: (id: string) => Promise<void>,
  fetchCheckout: (id: string, expiresAt?: string | null) => Promise<void>,
  hasActiveCheckout?: () => boolean,
): Promise<void> {
  // Judged on every tool of the turn: add_to_cart followed by get_cart still
  // changed the cart, and the itinerary card under that reply renders the live
  // checkout payload — a stale one would hide the item just added.
  const tools = meta.turnTools ?? (meta.lastTool ? [meta.lastTool] : []);
  const cartMutated = tools.some((t) => CART_MUTATING_TOOLS.has(t));

  // The BE cart is the source of truth, and this turn just changed it: any
  // checkout uuid minted earlier now points at the previous itinerary. Drop it
  // synchronously, before anything can await/throw, so the next "Complete
  // booking" click is guaranteed to re-ask the BE.
  if (cartMutated) {
    invalidateBotCheckoutCache(sessionId);
  }

  await refreshSelections(sessionId);

  if (tools.some((t) => CHECKOUT_TOOLS.has(t))) {
    const expiresAt = tools.includes('generate_checkout_url')
      ? meta.checkoutReady?.expires_at
      : undefined;
    await fetchCheckout(sessionId, expiresAt);
    return;
  }

  if (cartMutated && hasActiveCheckout?.()) {
    await fetchCheckout(sessionId);
  }
}
